import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { inr, formatDate } from "@/lib/api";
import DashboardLayout from "@/components/DashboardLayout";
import { useRealtime } from "@/lib/realtime";
import { OverviewPanel } from "@/pages/dist/DashboardPro";
import DemoIbaCard from "@/components/admin/DemoIbaCard";
import AdminClosingCard from "@/components/admin/AdminClosingCard";
import { Users, ShoppingCart, IdCard, BadgeIndianRupee, Package, TrendingUp, Zap, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

export default function AdminDashboard() {
  const [d, setD] = useState(null);
  const [closing, setClosing] = useState(false);
  const [closings, setClosings] = useState([]);
  const [myOverview, setMyOverview] = useState(null);
  const { subscribe } = useRealtime();

  const refetch = useCallback(() => {
    // api and setState setters are stable module refs — safe with [] deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    api.get("/admin/dashboard").then((r) => setD(r.data));
    api.get("/admin/closings").then((r) => setClosings(r.data)).catch(() => {});
    api.get("/dist/overview").then((r) => setMyOverview(r.data)).catch(() => {});
  }, []);

  useEffect(() => { refetch(); }, [refetch]);

  // Live: refresh on new orders / kyc / payout events
  useEffect(() => {
    let pending = null;
    const debounced = () => {
      if (pending) clearTimeout(pending);
      pending = setTimeout(() => { refetch(); pending = null; }, 600);
    };
    const offs = [
      subscribe("admin.order", (e) => { debounced(); toast.info(`New order ${e.data?.order_no}`, { description: `${e.data?.user_code} · ₹${e.data?.total}` }); }),
      subscribe("kyc.update", debounced),
      subscribe("payout.update", debounced),
      subscribe("closing.completed", debounced),
    ];
    return () => { offs.forEach((o) => o()); if (pending) clearTimeout(pending); };
  }, [subscribe, refetch]);

  const runClosing = async () => {
    if (!window.confirm("Run weekly closing now? This will carry-forward stronger legs, flush weak legs, and recompute ranks for all active distributors.")) return;
    setClosing(true);
    try {
      const { data } = await api.post("/admin/run-closing");
      toast.success(`Closing done: ${data.summary.users_processed} users · ${data.summary.rank_upgrades} rank upgrades · ${inr(data.summary.reward_credited)} credited`);
      const { data: cl } = await api.get("/admin/closings");
      setClosings(cl);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setClosing(false); }
  };

  const stats = [
    { l: "Total Members", v: d?.total_members ?? 0, i: Users, c: "border-navy" },
    { l: "Active IBAs", v: d?.active_distributors ?? 0, i: TrendingUp, c: "border-olive" },
    { l: "Total Sales", v: inr(d?.total_sales ?? 0), i: BadgeIndianRupee, c: "border-gold" },
    { l: "Total Orders", v: d?.total_orders ?? 0, i: ShoppingCart, c: "border-rose" },
    { l: "Products", v: d?.total_products ?? 0, i: Package, c: "border-navy" },
    { l: "Total SP", v: d?.total_sp ?? 0, i: TrendingUp, c: "border-gold" },
    { l: "Pending KYC", v: d?.pending_kyc ?? 0, i: IdCard, c: "border-rose" },
    { l: "Pending Payouts", v: d?.pending_payouts ?? 0, i: BadgeIndianRupee, c: "border-rose" },
  ];

  return (
    <DashboardLayout title="Admin Overview" subtitle="Samara Direct Selling control center">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {stats.map((s) => (
          <div key={s.l} data-testid={`stat-${s.l.replaceAll(" ", "-").toLowerCase()}`} className={`bg-white border-l-4 ${s.c} border-y border-r border-border p-5 rounded-r-sm`}>
            <div className="flex items-center justify-between">
              <div className="text-[10px] uppercase tracking-widest text-navy/50">{s.l}</div>
              <s.i size={14} className="text-navy/40" />
            </div>
            <div className="font-serif text-2xl text-navy mt-3">{s.v}</div>
          </div>
        ))}
      </div>

      {/* Admin ops utilities */}
      <div className="mt-6 space-y-6">
        <AdminClosingCard />
        <DemoIbaCard />
      </div>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-white border border-border rounded-sm overflow-hidden lg:col-span-2">
          <div className="px-5 py-4 border-b border-border flex justify-between">
            <h3 className="font-serif text-lg text-navy">Recent orders</h3>
            <Link to="/admin/orders" className="text-xs text-gold hover:underline">View all →</Link>
          </div>
          <table className="w-full text-sm">
            <tbody>
              {(d?.recent_orders || []).map((o) => (
                <tr key={o.id} className="border-b border-border last:border-0">
                  <td className="px-4 py-3"><div className="text-navy">{o.order_no}</div><div className="text-[10px] text-navy/50">{formatDate(o.created_at)}</div></td>
                  <td className="px-4 py-3 text-navy/70">{o.user_code}</td>
                  <td className="px-4 py-3 text-right font-medium text-navy">{inr(o.total)}</td>
                </tr>
              ))}
              {(d?.recent_orders || []).length === 0 && <tr><td className="p-6 text-center text-navy/50">No orders yet</td></tr>}
            </tbody>
          </table>
        </div>
        <div className="bg-white border border-border rounded-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-border"><h3 className="font-serif text-lg text-navy">Recent closings</h3></div>
          {closings.length === 0 ? (
            <div className="p-6 text-sm text-navy/50">No closings run yet</div>
          ) : (
            <ul className="divide-y divide-border text-sm">
              {closings.slice(0, 6).map((c) => (
                <li key={c.id} className="px-4 py-3">
                  <div className="text-xs text-navy/50">{formatDate(c.ran_at)}</div>
                  <div className="text-navy mt-0.5">{c.users_processed} users · {c.rank_upgrades} upgrades</div>
                  <div className="text-[10px] text-gold-deep">Reward credited: {inr(c.reward_credited)}</div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {myOverview && (
        <div className="mt-10" data-testid="admin-personal-overview">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h2 className="font-serif text-2xl text-navy">My Dashboard</h2>
              <p className="text-xs text-navy/50 mt-0.5">Personal binary tree, income, team activity — same view as your distributors.</p>
            </div>
            <RotatePasswordsBtn />
          </div>
          <OverviewPanel data={myOverview} />
        </div>
      )}
    </DashboardLayout>
  );
}

function RotatePasswordsBtn() {
  const CONFIRM_PHRASE = "ROTATE-ALL";
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);

  const isMatch = typed.trim() === CONFIRM_PHRASE;

  // Best-effort environment label from the backend URL (e.g. "wellness-direct-sell").
  let envLabel = "this environment";
  try {
    const host = new URL(process.env.REACT_APP_BACKEND_URL).host;
    envLabel = host.split(".")[0] || host;
  } catch (e) {
    // REACT_APP_BACKEND_URL is missing or malformed — falling back to the
    // generic label is intentional; log for debugging without alarming the user.
    console.debug("envLabel fallback:", e?.message);
  }

  const openDialog = () => {
    setTyped("");
    setOpen(true);
  };

  const doRotate = async () => {
    if (!isMatch || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/rotate-all-passwords");
      toast.success(`${data.count} passwords rotated — downloading PDF…`);
      const a = document.createElement("a");
      a.href = data.download_url;
      a.target = "_blank";
      a.rel = "noopener noreferrer";
      a.click();
      setOpen(false);
      setTyped("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rotation failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        onClick={openDialog}
        disabled={busy}
        data-testid="rotate-passwords-btn"
        className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest px-3 py-1.5 border border-rose/30 text-rose hover:bg-rose/5 rounded-sm disabled:opacity-50"
        title="Danger: rotates all passwords on this environment"
      >
        <ShieldAlert size={13} />
        {busy ? "Rotating…" : "Rotate all passwords"}
      </button>

      <Dialog open={open} onOpenChange={(v) => { if (!busy) setOpen(v); }}>
        <DialogContent data-testid="rotate-confirm-dialog" className="sm:max-w-lg">
          <DialogHeader>
            <div className="flex items-center gap-2 text-rose">
              <ShieldAlert size={20} />
              <DialogTitle className="font-serif text-xl">Danger — rotate ALL passwords?</DialogTitle>
            </div>
            <DialogDescription className="text-navy/70 leading-relaxed pt-2 space-y-2">
              <span className="block">
                You are about to reset the password of <b>every user</b> on
                {" "}<code className="px-1.5 py-0.5 bg-rose/10 text-rose rounded text-[11px]">{envLabel}</code>{" "}
                — including yourself, all admins, IBAs, franchises and customers.
              </span>
              <span className="block">
                Nobody will be able to log back in until they look up their <b>new</b> password in the
                credentials PDF that downloads immediately after rotation. Existing sessions continue
                to work until they expire.
              </span>
              <span className="block text-rose font-medium">
                This action cannot be undone. It is audit-logged with your admin ID.
              </span>
            </DialogDescription>
          </DialogHeader>

          <div className="mt-2">
            <label className="block text-[11px] uppercase tracking-widest text-navy/60 mb-1.5">
              Type <code className="px-1.5 py-0.5 bg-rose/10 text-rose rounded text-[11px]">{CONFIRM_PHRASE}</code> to confirm
            </label>
            <input
              type="text"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              placeholder={CONFIRM_PHRASE}
              autoComplete="off"
              autoCapitalize="characters"
              spellCheck={false}
              data-testid="rotate-confirm-input"
              className="w-full border border-border rounded-sm px-3 py-2 text-sm font-mono tracking-wider outline-none focus:border-rose focus:ring-2 focus:ring-rose/20"
              disabled={busy}
            />
          </div>

          <DialogFooter className="mt-4 gap-2 sm:gap-2">
            <button
              type="button"
              onClick={() => { setOpen(false); setTyped(""); }}
              disabled={busy}
              data-testid="rotate-cancel-btn"
              className="px-4 py-2 text-[11px] uppercase tracking-widest border border-border rounded-sm text-navy/70 hover:bg-navy/5 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={doRotate}
              disabled={!isMatch || busy}
              data-testid="rotate-confirm-btn"
              className="px-4 py-2 text-[11px] uppercase tracking-widest rounded-sm text-white bg-rose hover:bg-rose/90 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {busy ? "Rotating…" : "Yes, rotate all"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
